import type { PropsWithChildren, ReactElement } from 'react';
import React, { useEffect, useMemo, useState, isValidElement, cloneElement } from 'react';
import { parse } from 'date-fns';
import { Box } from '@mui/material';
import type { ChartReportProps, TableReportProps } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import { ChartDataKeyDateFormat } from 'c-reports/Generators';
import type { AllowedAction } from './ChartWrapperControls';
import ChartWrapperControls from './ChartWrapperControls';

export type ReportControlsState = {
    showPercent: boolean;
    showNumbers: boolean;
    viewFilter: 'Expected' | 'Delivered' | 'Both';
    cumulative: boolean;
};

type Props = {
    chartProps?: ChartReportProps<any>;
    tableProps?: TableReportProps<any>;
    type: ReportType;
    namePrefix: string;
    startDate: Date;
    endDate: Date;
    defaultShowNumbers?: boolean;
    hideControls?: boolean;
    controls?: ReportControlsState;
    onControlsChange?: (controls: ReportControlsState) => void;
    /** Enables the "Accumulate" toolbar toggle (line charts only); de-accumulates when turned off. */
    allowAccumulateToggle?: boolean;
    initialSort?: SortMode;
};

type SortMode = 'asc' | 'desc' | 'alpha' | null;
type ViewFilter = 'Expected' | 'Delivered' | 'Both';

const shouldDefaultToSourceOrder = (namePrefix: string) =>
    [
        'by_hour_overall',
        'hour_overall',
        'dow_overall',
        'by_weekday_overall',
        'by_day_of_week',
        'by_hour',
    ].some(key => namePrefix.includes(key));

const hasDateXAxisValues = (chartProps?: ChartReportProps<any>) => {
    const rows = Array.isArray(chartProps?.data) ? chartProps.data : [];
    const xKey = chartProps?.xAxisDataKey ?? 'name';

    if (rows.length === 0) return false;

    return rows.every(row => {
        const value = row?.[xKey];
        if (typeof value !== 'string' || value.trim().length === 0) return false;

        const parsed = parse(value, ChartDataKeyDateFormat, new Date());
        return !Number.isNaN(parsed.getTime());
    });
};

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, '');

const baseName = (s: string) => {
    const n = norm(s);
    const parts = n.split(
        /(expected|impressions|impacts|plays|timeplayed|time|percentage|actual|delivered|total)/,
    );
    return parts[0];
};

const keyOf = (part: any) => (part?.labelOverride ?? part?.dataKey ?? '') as string;
const isExpectedKeyLocal = (k: string) => /expected/i.test(k);
const isPercentKeyLocal = (k: string) => /(%|percentage)/i.test(k);
const roleOf = (part: any) => part?.filterRole as 'expected' | 'delivered' | undefined;

const isExpectedSeries = (part: any) =>
    roleOf(part) === 'expected' ||
    (roleOf(part) == null && (part?.comparisonSeries === true || isExpectedKeyLocal(keyOf(part))));

const isDeliveredSeries = (part: any) =>
    roleOf(part) === 'delivered' ||
    (roleOf(part) == null && !isExpectedSeries(part) && !isPercentKeyLocal(keyOf(part)));

const getSortValue = (row: any, key: string, showPercent: boolean) => {
    if (showPercent) {
        const pctValue = row?.[`${key}__pct`];
        if (pctValue === Number.POSITIVE_INFINITY) return Number.POSITIVE_INFINITY;

        const asNumber = Number(pctValue);
        if (!Number.isNaN(asNumber)) return asNumber;
    }

    const value = row?.[key];
    if (value === Number.POSITIVE_INFINITY) return Number.POSITIVE_INFINITY;

    const asNumber = Number(value ?? 0);
    return Number.isNaN(asNumber) ? 0 : asNumber;
};

const ReportControlsWrapper: React.FC<PropsWithChildren<Props>> = ({ children, ...props }) => {
    const preserveInitialDataOrder = useMemo(
        () => shouldDefaultToSourceOrder(props.namePrefix) || hasDateXAxisValues(props.chartProps),
        [props.chartProps, props.namePrefix],
    );
    const [sortMode, setSortMode] = useState<SortMode>(
        props.initialSort !== undefined
            ? props.initialSort
            : preserveInitialDataOrder
            ? null
            : 'desc',
    );
    const [internalControls, setInternalControls] = useState<ReportControlsState>({
        showPercent: false,
        showNumbers: props.defaultShowNumbers ?? false,
        viewFilter: 'Both',
        cumulative: true,
    });
    const controlled = props.controls;
    const showPercent = controlled ? controlled.showPercent : internalControls.showPercent;
    const showNumbers = controlled ? controlled.showNumbers : internalControls.showNumbers;
    const viewFilter = controlled ? controlled.viewFilter : internalControls.viewFilter;
    const cumulative = controlled ? controlled.cumulative : internalControls.cumulative;
    const updateControls = (patch: Partial<ReportControlsState>) => {
        if (controlled) {
            props.onControlsChange?.({
                showPercent,
                showNumbers,
                viewFilter,
                cumulative,
                ...patch,
            });
        } else {
            setInternalControls(s => ({ ...s, ...patch }));
        }
    };

    useEffect(() => {
        setSortMode(preserveInitialDataOrder ? null : 'desc');
    }, [preserveInitialDataOrder, props.namePrefix]);

    useEffect(() => {
        if (!props.controls) {
            setInternalControls(s => ({ ...s, showNumbers: props.defaultShowNumbers ?? false }));
        }
    }, [props.defaultShowNumbers, props.namePrefix, props.controls]);

    const { hasBothExpectedAndDelivered, isPurePercentChart } = useMemo(() => {
        const origParts = (props.chartProps?.parts ?? []) as any[];
        const labels = origParts.map(keyOf);

        const hasExpected = origParts.some(isExpectedSeries);
        const hasDelivered = origParts.some(isDeliveredSeries);

        const purePercent =
            props.type === ReportType.Line &&
            origParts.length === 1 &&
            /(%|percentage)/i.test((labels[0] ?? '') as string);

        return {
            hasBothExpectedAndDelivered: hasExpected && hasDelivered,
            isPurePercentChart: purePercent,
        };
    }, [props.chartProps?.parts, props.type]);
    const allowPercentToggle = props.chartProps?.allowPercentToggle === true;
    const percentOfTotal = props.chartProps?.percentOfTotal === true;
    const percentOfTotalLabel = props.chartProps?.percentOfTotalLabel;

    const allowedActions = useMemo<AllowedAction[]>(() => {
        let base: AllowedAction[];

        switch (props.type) {
            case ReportType.Table:
                base = ['download'];
                break;
            case ReportType.Line:
                base = ['download', 'togglePercent', 'toggleNumbers', 'filter', 'exportData'];
                break;
            case ReportType.Bar:
            case ReportType.Mixed:
                base = [
                    'download',
                    'sortAsc',
                    'sortDesc',
                    'sortAlpha',
                    'togglePercent',
                    'toggleNumbers',
                    'filter',
                    'exportData',
                ];
                break;
            case ReportType.Pie:
                base = ['download', 'filter', 'exportData'];
                break;
            default:
                base = ['download', 'exportData'];
        }

        if (!hasBothExpectedAndDelivered && !allowPercentToggle) {
            base = base.filter(a => a !== 'filter');
            base = base.filter(a => a !== 'togglePercent');
        } else if (!hasBothExpectedAndDelivered && allowPercentToggle) {
            base = base.filter(a => a !== 'filter');
        }

        if (isPurePercentChart) {
            base = base.filter(a => a !== 'togglePercent').filter(a => a !== 'toggleNumbers');
        }

        if (props.allowAccumulateToggle && props.type === ReportType.Line) {
            base = [...base, 'toggleAccumulate'];
        }

        return base;
    }, [
        props.type,
        props.allowAccumulateToggle,
        hasBothExpectedAndDelivered,
        allowPercentToggle,
        isPurePercentChart,
    ]);

    const { visibleParts, transformedData } = useMemo(() => {
        const origParts = (props.chartProps?.parts ?? []) as any[];
        const origData = Array.isArray(props.chartProps?.data)
            ? (props.chartProps.data as any[])
            : [];

        const byBase = origParts.reduce((acc, p) => {
            const k = keyOf(p);
            const b = baseName(k);
            (acc[b] ??= []).push({
                displayKey: k,
                sourceKey: (p?.dataKey ?? k) as string,
                part: p,
            });
            return acc;
        }, {} as Record<string, { displayKey: string; sourceKey: string; part: any }[]>);
        const comparisonParts = origParts.filter(isExpectedSeries).map(part => ({
            displayKey: keyOf(part),
            sourceKey: (part?.dataKey ?? keyOf(part)) as string,
            part,
        }));

        let partsOut = origParts.filter(p => {
            if (viewFilter === 'Expected') return isExpectedSeries(p);
            if (viewFilter === 'Delivered') return isDeliveredSeries(p);
            return true;
        });

        if (showPercent) {
            partsOut = partsOut.filter(p => !isExpectedSeries(p));
        }

        const rows = origData.map(r => ({ ...r }));

        // De-accumulate a running-total line into per-day deltas (before percent/sort).
        if (props.allowAccumulateToggle && props.type === ReportType.Line && !cumulative) {
            const seriesKeys = new Set<string>();
            origParts.forEach(p => {
                const dk = (p?.dataKey ?? keyOf(p)) as string;
                if (dk) seriesKeys.add(dk);
                const disp = keyOf(p);
                if (disp) seriesKeys.add(disp);
            });
            seriesKeys.forEach(key => {
                for (let i = rows.length - 1; i > 0; i -= 1) {
                    const cur = Number(origData[i]?.[key]);
                    const prev = Number(origData[i - 1]?.[key]);
                    if (Number.isFinite(cur) && Number.isFinite(prev)) {
                        rows[i][key] = cur - prev;
                    }
                }
            });
        }

        if (showPercent && hasBothExpectedAndDelivered) {
            Object.values(byBase as any[]).forEach(group => {
                const expected =
                    group.find((g: any) => isExpectedSeries(g.part)) ??
                    (comparisonParts.length === 1 ? comparisonParts[0] : undefined);
                group
                    .filter((g: any) => isDeliveredSeries(g.part))
                    .forEach(({ displayKey, sourceKey }) => {
                        const pctKey = `${displayKey}__pct`;
                        rows.forEach(row => {
                            const d = Number(row[sourceKey] ?? row[displayKey] ?? 0);
                            const e = Number(
                                expected
                                    ? row[expected.sourceKey] ?? row[expected.displayKey] ?? 0
                                    : 0,
                            );
                            // eslint-disable-next-line no-param-reassign
                            row[pctKey] =
                                e > 0 ? (d / e) * 100 : d > 0 ? Number.POSITIVE_INFINITY : 0;
                            // eslint-disable-next-line no-param-reassign
                            row[`${sourceKey}__baseValue`] = e;
                            // eslint-disable-next-line no-param-reassign
                            row[`${sourceKey}__baseLabel`] = expected ? expected.displayKey : '';
                        });
                    });
            });
        }

        if (showPercent && percentOfTotal) {
            partsOut.filter(isDeliveredSeries).forEach(part => {
                const displayKey = keyOf(part);
                const sourceKey = (part?.dataKey ?? displayKey) as string;
                const total = rows.reduce(
                    (sum, row) => sum + Number(row[sourceKey] ?? row[displayKey] ?? 0),
                    0,
                );
                const baseLabel = percentOfTotalLabel ?? displayKey;
                rows.forEach(row => {
                    const val = Number(row[sourceKey] ?? row[displayKey] ?? 0);
                    const pct = total > 0 ? (val / total) * 100 : 0;
                    // eslint-disable-next-line no-param-reassign
                    row[`${displayKey}__pct`] = pct;
                    // eslint-disable-next-line no-param-reassign
                    row[`${sourceKey}__pct`] = pct;
                    // eslint-disable-next-line no-param-reassign
                    row[`${displayKey}__baseLabel`] = baseLabel;
                    // eslint-disable-next-line no-param-reassign
                    row[`${sourceKey}__baseLabel`] = baseLabel;
                    // eslint-disable-next-line no-param-reassign
                    row[`${displayKey}__baseValue`] = total;
                    // eslint-disable-next-line no-param-reassign
                    row[`${sourceKey}__baseValue`] = total;
                });
            });
        }

        if (showPercent) {
            partsOut.filter(isDeliveredSeries).forEach(part => {
                const displayKey = keyOf(part);
                const sourceKey = (part?.dataKey ?? displayKey) as string;
                const pctKey = `${displayKey}__pct`;

                rows.forEach(row => {
                    const pctValue = Number(row?.[pctKey]);

                    if (!Number.isNaN(pctValue)) {
                        const plottedValue = Number.isFinite(pctValue) ? pctValue : null;

                        const rawFull = Number(row[sourceKey] ?? row[displayKey] ?? 0);
                        // eslint-disable-next-line no-param-reassign
                        row[`${sourceKey}__full`] = rawFull;

                        // eslint-disable-next-line no-param-reassign
                        row[sourceKey] = plottedValue;

                        if (displayKey !== sourceKey) {
                            // eslint-disable-next-line no-param-reassign
                            row[`${displayKey}__full`] = rawFull;
                            // eslint-disable-next-line no-param-reassign
                            row[displayKey] = plottedValue;
                        }
                    }
                });
            });
        }

        const xKey = props.chartProps?.xAxisDataKey ?? 'name';
        if (props.type !== ReportType.Line && sortMode) {
            const expectedKeys = partsOut
                .filter(isExpectedSeries)
                .map(p => p.dataKey || p.labelOverride);

            const deliveredKeys = partsOut
                .filter(isDeliveredSeries)
                .map(p => p.dataKey || p.labelOverride);

            let sortKey: string | undefined;

            if (viewFilter === 'Expected' && expectedKeys.length === 1) {
                sortKey = expectedKeys[0];
            } else if (props.chartProps?.sortDataKey) {
                sortKey = props.chartProps.sortDataKey;
            } else if (deliveredKeys.length > 0) {
                sortKey = deliveredKeys[0];
            } else if (expectedKeys.length > 0) {
                sortKey = expectedKeys[0];
            }

            if (!sortKey && partsOut.length > 0) {
                const first = partsOut[0];
                sortKey = (first.labelOverride ?? first.dataKey) as string;
            }

            if (sortKey && sortMode !== 'alpha') {
                const key = sortKey;
                rows.sort((a, b) => {
                    const A = getSortValue(a, key, showPercent);
                    const B = getSortValue(b, key, showPercent);
                    return sortMode === 'asc' ? A - B : B - A;
                });
            }

            if (sortMode === 'alpha') {
                rows.sort((a, b) =>
                    String(a?.[xKey] ?? '').localeCompare(String(b?.[xKey] ?? ''), undefined, {
                        numeric: true,
                    }),
                );
            }
        }

        return { visibleParts: partsOut, transformedData: rows };
    }, [
        props.chartProps,
        props.allowAccumulateToggle,
        cumulative,
        viewFilter,
        showPercent,
        percentOfTotal,
        percentOfTotalLabel,
        sortMode,
        props.type,
        hasBothExpectedAndDelivered,
    ]);

    const percentChartMeta = useMemo(() => {
        const rows = props.chartProps?.data ?? [];
        const first = rows[0] ?? {};

        const percentKey = Object.keys(first).find(k => /percentage/i.test(k));
        const totalKey = Object.keys(first).find(k =>
            /(total).*?(impacts|plays|time|duration)/i.test(k),
        );

        return {
            isPercent: Boolean(percentKey && totalKey),
            percentKey,
            totalKey,
        };
    }, [props.chartProps?.data]);

    const effectiveChartProps: ChartReportProps<any> | undefined = useMemo(() => {
        if (!props.chartProps) return undefined;

        const labelsOn = showNumbers || showPercent;
        const valueLabelType = showPercent ? 'percent' : 'number';

        const base: any = {
            ...props.chartProps,
            parts: visibleParts as any,
            data: transformedData as any,
            showValueLabels: labelsOn,
            valueLabelType,
            valuesArePercent: showPercent,
        };

        if (showPercent && percentChartMeta.isPercent) {
            base.valuesArePercent = true;
            base.percentDataKey = percentChartMeta.percentKey;
            base.totalDataKey = percentChartMeta.totalKey;
        }

        const origParts = (props.chartProps.parts ?? []) as any[];
        const origLabels = origParts.map(keyOf);
        const singleSeriesIsPercent =
            props.type === ReportType.Bar &&
            origParts.length === 1 &&
            isPercentKeyLocal(origLabels[0] ?? '');

        if (showPercent && singleSeriesIsPercent) {
            base.valuesArePercent = true;
            base.percentDataKey = origLabels[0];
        }

        return base;
    }, [
        props.chartProps,
        visibleParts,
        transformedData,
        showNumbers,
        showPercent,
        percentChartMeta,
        props.type,
    ]);

    const onExportData = (format: 'json' | 'csv') => {
        if (props.chartProps?.exportData) {
            const exportData = props.chartProps.exportData;
            const headers = Object.keys(exportData[0] ?? {});

            if (format === 'json') {
                const blob = new Blob([JSON.stringify(exportData, null, 2)], {
                    type: 'application/json',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${props.namePrefix}_${format}.json`;
                a.click();
                URL.revokeObjectURL(url);
                return;
            }

            const lines = [
                headers.join(','),
                ...exportData.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')),
            ];
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${props.namePrefix}_${format}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        const data = transformedData ?? [];
        const xKey = props.chartProps?.xAxisDataKey ?? 'name';
        const seriesKeys = visibleParts.map(keyOf);

        const minimal = data.map(row => {
            const obj: Record<string, any> = { [xKey]: row?.[xKey] };
            // eslint-disable-next-line no-return-assign
            seriesKeys.forEach(k => (obj[k] = row?.[k]));
            return obj;
        });

        if (format === 'json') {
            const blob = new Blob([JSON.stringify(minimal, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${props.namePrefix}_${format}.json`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        const headers = [xKey, ...seriesKeys];
        const lines = [
            headers.join(','),
            ...minimal.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${props.namePrefix}_${format}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const toggleSort = (mode: SortMode) => setSortMode(cur => (cur === mode ? null : mode));

    const handlers = {
        onSortAsc: () => toggleSort('asc'),
        onSortDesc: () => toggleSort('desc'),
        onSortAlpha: () => toggleSort('alpha'),
        onTogglePercent: () => updateControls({ showPercent: !showPercent }),
        onToggleNumbers: () => updateControls({ showNumbers: !showNumbers }),
        onToggleAccumulate: () => updateControls({ cumulative: !cumulative }),
        onFilterChange: (v: ViewFilter) => updateControls({ viewFilter: v }),
        onExportData,
    };

    const childWithOverrides = useMemo(() => {
        if (props.type === ReportType.Table) return children;
        if (!isValidElement(children as any)) return children;

        const injectedProps = (effectiveChartProps ?? props.chartProps) as ChartReportProps<any>;

        return cloneElement(children as ReactElement<any>, {
            ...injectedProps,
        });
    }, [children, props.type, effectiveChartProps, props.chartProps]);
    const controlsExtras = props.tableProps?.controlsExtras ?? props.chartProps?.controlsExtras;

    return (
        <Box height={props.hideControls ? '100%' : '92%'} width="100%" position="relative">
            {!props.hideControls && allowedActions.length > 0 && (
                <Box
                    position="relative"
                    display="flex"
                    justifyContent="flex-end"
                    top={0}
                    right={20}
                    sx={{ pb: 1 }}
                >
                    <ChartWrapperControls
                        {...props}
                        chartProps={effectiveChartProps ?? props.chartProps}
                        filterOptionLabels={props.chartProps?.filterOptionLabels}
                        allowedActions={allowedActions}
                        {...handlers}
                        percentOn={showPercent}
                        numbersOn={showNumbers}
                        accumulateOn={cumulative}
                        activeSort={sortMode}
                        activeFilter={viewFilter}
                    />
                    {controlsExtras}
                </Box>
            )}
            {childWithOverrides}
        </Box>
    );
};

export default ReportControlsWrapper;
